import { useCallback, useEffect, useState } from "react";
import {
  fetchPartnerVehicleMakes,
  fetchPartnerVehicleModels,
  fetchPartnerVehicleTrims,
} from "../../services/automapPartnerDealerApi";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  applyMakeSelection,
  applyModelSelection,
  applyTrimSelection,
  IDENTITY_MODE,
  partnerVehicleDisplayName,
  switchToManualIdentity,
  switchToSelectorIdentity,
} from "../../utils/partnerListingVehicleIdentity";

function LookupField({
  label,
  required = false,
  optional = false,
  disabled = false,
  searchValue,
  onSearchChange,
  loading,
  error,
  emptyMessage,
  options = [],
  selectedId,
  onSelect,
  placeholder,
}) {
  return (
    <div className="partner-vehicle-identity__field">
      <label>
        {label}
        {required ? " *" : ""}
        {optional ? " (optional)" : ""}
        <input
          type="search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
      </label>
      {loading ? <p className="partner-vehicle-identity__hint muted">Loading…</p> : null}
      {error ? <p className="partner-vehicle-identity__hint partner-vehicle-identity__hint--error">{error}</p> : null}
      {!loading && !error && options.length === 0 && searchValue ? (
        <p className="partner-vehicle-identity__hint muted">{emptyMessage}</p>
      ) : null}
      {options.length ? (
        <ul className="partner-vehicle-identity__options" role="listbox">
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={`partner-vehicle-identity__option${
                  selectedId === option.id ? " is-selected" : ""
                }`}
                onClick={() => onSelect(option)}
                disabled={disabled}
              >
                {option.label}
                {option.aliasMatched ? (
                  <span className="partner-vehicle-identity__alias">alias match</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function PartnerVehicleIdentityFields({
  identity,
  onChange,
  year = "",
  disabled = false,
  liveListing = false,
}) {
  const manualMode = identity.mode === IDENTITY_MODE.MANUAL;

  const [makeSearch, setMakeSearch] = useState(identity.makeLabel || identity.makeRaw || "");
  const [modelSearch, setModelSearch] = useState(identity.modelLabel || identity.modelRaw || "");
  const [trimSearch, setTrimSearch] = useState(identity.trimLabel || identity.trimRaw || "");

  const debouncedMakeSearch = useDebouncedValue(makeSearch);
  const debouncedModelSearch = useDebouncedValue(modelSearch);
  const debouncedTrimSearch = useDebouncedValue(trimSearch);

  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [trimOptions, setTrimOptions] = useState([]);

  const [makeLoading, setMakeLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [trimLoading, setTrimLoading] = useState(false);

  const [makeError, setMakeError] = useState("");
  const [modelError, setModelError] = useState("");
  const [trimError, setTrimError] = useState("");

  useEffect(() => {
    setMakeSearch(identity.makeLabel || identity.makeRaw || "");
    setModelSearch(identity.modelLabel || identity.modelRaw || "");
    setTrimSearch(identity.trimLabel || identity.trimRaw || "");
  }, [identity.makeId, identity.modelId, identity.trimId, identity.mode]);

  useEffect(() => {
    if (manualMode || disabled) return undefined;

    let cancelled = false;
    setMakeLoading(true);
    setMakeError("");

    fetchPartnerVehicleMakes({ search: debouncedMakeSearch || undefined, limit: 25 })
      .then((result) => {
        if (cancelled) return;
        setMakeOptions(result.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMakeOptions([]);
        setMakeError(err.message || "Unable to load makes.");
      })
      .finally(() => {
        if (!cancelled) setMakeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedMakeSearch, manualMode, disabled]);

  useEffect(() => {
    if (manualMode || disabled || !identity.makeId) {
      setModelOptions([]);
      return undefined;
    }

    let cancelled = false;
    setModelLoading(true);
    setModelError("");

    fetchPartnerVehicleModels({
      makeId: identity.makeId,
      search: debouncedModelSearch || undefined,
      limit: 50,
    })
      .then((result) => {
        if (cancelled) return;
        setModelOptions(result.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setModelOptions([]);
        setModelError(err.message || "Unable to load models.");
      })
      .finally(() => {
        if (!cancelled) setModelLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [identity.makeId, debouncedModelSearch, manualMode, disabled]);

  useEffect(() => {
    if (manualMode || disabled || !identity.modelId) {
      setTrimOptions([]);
      return undefined;
    }

    let cancelled = false;
    setTrimLoading(true);
    setTrimError("");

    fetchPartnerVehicleTrims({
      modelId: identity.modelId,
      year: year || undefined,
      search: debouncedTrimSearch || undefined,
      limit: 50,
    })
      .then((result) => {
        if (cancelled) return;
        setTrimOptions(result.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTrimOptions([]);
        setTrimError(err.message || "Unable to load trims.");
      })
      .finally(() => {
        if (!cancelled) setTrimLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [identity.modelId, year, debouncedTrimSearch, manualMode, disabled]);

  useEffect(() => {
    if (!liveListing || manualMode || disabled || !identity.modelId || identity.trimId) return undefined;

    const trimmed = debouncedTrimSearch.trim();
    const currentTrim = String(identity.trimLabel || identity.trimRaw || "").trim();
    if (trimmed === currentTrim) return undefined;

    const matchesOption = trimOptions.some(
      (option) => option.label.toLowerCase() === trimmed.toLowerCase()
    );
    if (matchesOption) return undefined;

    onChange({
      ...identity,
      trimId: null,
      trimLabel: trimmed || null,
      trimRaw: trimmed,
    });
    return undefined;
  }, [
    debouncedTrimSearch,
    disabled,
    identity.modelId,
    identity.trimId,
    identity.trimLabel,
    identity.trimRaw,
    liveListing,
    manualMode,
    onChange,
    trimOptions,
    identity,
  ]);

  const handleMakeSelect = useCallback(
    (make) => {
      onChange(applyMakeSelection(identity, make));
      setMakeSearch(make.label);
      setModelSearch("");
      setTrimSearch("");
    },
    [identity, onChange]
  );

  const handleModelSelect = useCallback(
    (model) => {
      onChange(applyModelSelection(identity, model));
      setModelSearch(model.label);
      setTrimSearch("");
    },
    [identity, onChange]
  );

  const handleTrimSelect = useCallback(
    (trim) => {
      onChange(applyTrimSelection(identity, trim));
      setTrimSearch(trim.label);
    },
    [identity, onChange]
  );

  const displayName = partnerVehicleDisplayName(identity, year);
  const showNeedsReview =
    identity.needsReview ||
    identity.normalizationStatus === "partial" ||
    identity.normalizationStatus === "unmatched" ||
    manualMode;

  return (
    <section className="partner-vehicle-identity" aria-label="Vehicle identity">
      <div className="partner-vehicle-identity__header">
        <h3>Vehicle identity</h3>
        <p className="muted">Select Make → Model → Trim from the standard catalog.</p>
      </div>

      {!manualMode ? (
        <>
          <LookupField
            label="Make"
            required
            disabled={disabled}
            searchValue={makeSearch}
            onSearchChange={setMakeSearch}
            loading={makeLoading}
            error={makeError}
            emptyMessage="No makes found. Try manual entry below."
            options={makeOptions}
            selectedId={identity.makeId}
            onSelect={handleMakeSelect}
            placeholder="Search makes…"
          />

          <LookupField
            label="Model"
            required
            disabled={disabled || !identity.makeId}
            searchValue={modelSearch}
            onSearchChange={setModelSearch}
            loading={modelLoading}
            error={modelError}
            emptyMessage={
              identity.makeId ? "No models found for this make." : "Select a make first."
            }
            options={modelOptions}
            selectedId={identity.modelId}
            onSelect={handleModelSelect}
            placeholder={identity.makeId ? "Search models…" : "Select a make first"}
          />

          <LookupField
            label="Trim"
            optional
            disabled={disabled || !identity.modelId}
            searchValue={trimSearch}
            onSearchChange={setTrimSearch}
            loading={trimLoading}
            error={trimError}
            emptyMessage={
              identity.modelId
                ? liveListing
                  ? "No catalog trim found. Type the trim name above and save."
                  : "No trims found. You can leave trim blank or use manual entry."
                : "Select a model first."
            }
            options={trimOptions}
            selectedId={identity.trimId}
            onSelect={handleTrimSelect}
            placeholder={identity.modelId ? "Search trims (optional)…" : "Select a model first"}
          />
        </>
      ) : (
        <div className="partner-dealer-form__grid partner-dealer-form__grid--single">
          <label>
            Make (manual) *
            <input
              value={identity.makeRaw}
              onChange={(event) =>
                onChange({ ...identity, makeRaw: event.target.value, needsReview: true })
              }
              required
              disabled={disabled}
            />
          </label>
          <label>
            Model (manual) *
            <input
              value={identity.modelRaw}
              onChange={(event) =>
                onChange({ ...identity, modelRaw: event.target.value, needsReview: true })
              }
              required
              disabled={disabled}
            />
          </label>
          <label>
            Trim (manual)
            <input
              value={identity.trimRaw}
              onChange={(event) =>
                onChange({ ...identity, trimRaw: event.target.value, needsReview: true })
              }
              disabled={disabled}
            />
          </label>
        </div>
      )}

      <div className="partner-vehicle-identity__fallback">
        {!manualMode && !liveListing ? (
          <button
            type="button"
            className="cv-button-link"
            onClick={() => onChange(switchToManualIdentity(identity))}
            disabled={disabled}
          >
            Can&apos;t find the exact vehicle? Enter it manually
          </button>
        ) : !manualMode && liveListing ? (
          <p className="partner-vehicle-identity__hint muted">
            Imported listings keep catalog Make and Model. Type a trim name above when no catalog
            trim is available.
          </p>
        ) : (
          <button
            type="button"
            className="cv-button-link"
            onClick={() => onChange(switchToSelectorIdentity(identity))}
            disabled={disabled}
          >
            Back to standard Make / Model / Trim selectors
          </button>
        )}
      </div>

      {manualMode ? (
        <p className="partner-vehicle-identity__warning">
          We&apos;ll save this listing, but the vehicle name may need review before it appears in
          grouped inventory.
        </p>
      ) : null}

      {identity.normalizationStatus === "partial" && !manualMode ? (
        <p className="partner-vehicle-identity__warning">
          This listing has partial vehicle identity and may need review.
        </p>
      ) : null}

      <div className="partner-vehicle-identity__preview">
        <p className="partner-vehicle-identity__preview-label">Vehicle display name</p>
        <p className="partner-vehicle-identity__preview-value">
          {displayName || "—"}
          {showNeedsReview ? (
            <span className="partner-vehicle-identity__review-badge">Needs review</span>
          ) : null}
        </p>
      </div>
    </section>
  );
}
